import { TypedDocumentNode, gql } from '@apollo/client';
import { useMemo } from 'react';
import { Outlet, generatePath } from 'react-router-dom';

import { useTypedParams } from '@sorare/routing';

import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import {
  PlayerPageTabs,
  Tabs,
  Props as TabsProps,
} from '@sorare/core/src/components/seo/Player/Tabs';
import { RotoWireRedirect } from '@sorare/core/src/components/seo/redirects/RotoWireRedirect';
import {
  NBA_PLAYERS_SLUG,
  NBA_PLAYERS_SLUG_CARDS,
  NBA_PLAYERS_SLUG_GAMES,
  NBA_PLAYERS_SLUG_TRANSACTIONS,
} from '@sorare/core/src/constants/__generated__/routes';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { metadatas } from '@sorare/core/src/lib/seo/common';

import { useAnySportEvents } from '@sorare/gameplay/src/lib/events';

import {
  NbaSeoPlayerQuery,
  NbaSeoPlayerQueryVariables,
} from './__generated__/layout.graphql';
import { LayoutParams } from './__generated__/routeParams';

const NBA_SEO_PLAYER_QUERY = gql`
  query NbaSeoPlayerQuery($slug: String!) {
    anyPlayer(slug: $slug) {
      slug
      displayName
      avatarPictureUrl
    }
  }
` as TypedDocumentNode<NbaSeoPlayerQuery, NbaSeoPlayerQueryVariables>;

const NbaPlayer = () => {
  const { slug } = useTypedParams<LayoutParams>();
  const { data, loading } = useQuery(NBA_SEO_PLAYER_QUERY, {
    variables: {
      slug,
    },
  });
  const track = useAnySportEvents();

  const anyPlayer = data?.anyPlayer;

  useTitleAndDescription(
    metadatas.player.title,
    metadatas.player.description,
    {
      display_name: anyPlayer?.displayName,
    },
    {
      img: anyPlayer?.avatarPictureUrl,
    }
  );

  const tabs: TabsProps['tabs'] = useMemo(() => {
    return {
      [PlayerPageTabs.OVERVIEW]: {
        to: generatePath(NBA_PLAYERS_SLUG, {
          slug,
        }),
      },
      [PlayerPageTabs.GAMES]: {
        to: generatePath(NBA_PLAYERS_SLUG_GAMES, {
          slug,
        }),
      },
      [PlayerPageTabs.TRANSACTIONS]: {
        to: generatePath(NBA_PLAYERS_SLUG_TRANSACTIONS, {
          slug,
        }),
      },
      [PlayerPageTabs.CARDS]: {
        to: generatePath(NBA_PLAYERS_SLUG_CARDS, {
          slug,
        }),
        onClick: () =>
          track('Click Player Page Cards Tab', { playerSlug: slug }),
      },
    };
  }, [slug, track]);

  if (loading) return <LoadingIndicator fullHeight />;
  if (!anyPlayer) return null;

  return (
    <>
      <PageHeader text={anyPlayer.displayName} />
      <RotoWireRedirect />
      <ContentWithStickyHeader header={<Tabs tabs={tabs} />}>
        <Outlet />
      </ContentWithStickyHeader>
    </>
  );
};

export default NbaPlayer;
